import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map, catchError, throwError } from 'rxjs';
import { ImportantAlert, TargetAudience } from '../models/important-alert.interface';

@Injectable({
  providedIn: 'root',
})
export class ImportantAlertService {
  constructor(private readonly apollo: Apollo) {}

  getUnviewedAlerts(): Observable<ImportantAlert[]> {
    return this.apollo
      .query<{ unviewedImportantAlerts: ImportantAlert[] }>({
      query: gql`
          query GetUnviewedImportantAlerts {
            unviewedImportantAlerts {
              id
              title
              message
              targetAudience
              isActive
              createdAt
              createdByUserId
            }
          }
        `,
      fetchPolicy: 'network-only',
    })
      .pipe(
        map((result) => result.data?.unviewedImportantAlerts ?? []),
        catchError((error) => {
          console.error('Error fetching unviewed alerts:', error);
          return throwError(() => error);
        })
      );
  }

  markAsViewed(alertId: number): Observable<boolean> {
    return this.apollo
      .mutate<{ markImportantAlertAsViewed: boolean }>({
      mutation: gql`
          mutation MarkImportantAlertAsViewed($alertId: Long!) {
            markImportantAlertAsViewed(alertId: $alertId)
          }
        `,
      variables: { alertId },
    })
      .pipe(
        map((result) => result.data?.markImportantAlertAsViewed ?? false),
        catchError((error) => {
          console.error('Error marking alert as viewed:', error);
          return throwError(() => error);
        })
      );
  }

  createAlert(
    title: string,
    message: string,
    targetAudience: TargetAudience
  ): Observable<ImportantAlert> {
    // HotChocolate convertit les enums C# en SCREAMING_SNAKE_CASE pour GraphQL
    // All -> ALL, VetsOnly -> VETS_ONLY
    // Keeping the ternary here as it maps between two different enum systems (TypeScript to GraphQL)
    // and changing it would reduce code clarity
    const targetAudienceGraphQL = targetAudience === TargetAudience.All ? 'ALL' : 'VETS_ONLY';

    return this.apollo
      .mutate<{ createImportantAlert: ImportantAlert }>({
      mutation: gql`
          mutation CreateImportantAlert(
            $title: String!
            $message: String!
            $targetAudience: ETargetAudience!
          ) {
            createImportantAlert(
              title: $title
              message: $message
              targetAudience: $targetAudience
            ) {
              id
              title
              message
              targetAudience
              isActive
              createdAt
              createdByUserId
            }
          }
        `,
      variables: { title, message, targetAudience: targetAudienceGraphQL },
    })
      .pipe(
        map((result) => {
          if (!result.data?.createImportantAlert) {
            throw new Error('Failed to create alert');
          }

          return result.data.createImportantAlert;
        }),
        catchError((error) => {
          console.error('Error creating alert:', error);
          return throwError(() => error);
        })
      );
  }

  deactivateAlert(alertId: number): Observable<boolean> {
    return this.apollo
      .mutate<{ deactivateImportantAlert: boolean }>({
      mutation: gql`
          mutation DeactivateImportantAlert($alertId: Long!) {
            deactivateImportantAlert(alertId: $alertId)
          }
        `,
      variables: { alertId },
    })
      .pipe(
        map((result) => result.data?.deactivateImportantAlert ?? false),
        catchError((error) => {
          console.error('Error deactivating alert:', error);
          return throwError(() => error);
        })
      );
  }

  getAllAlerts(): Observable<ImportantAlert[]> {
    return this.apollo
      .query<{ allImportantAlerts: ImportantAlert[] }>({
      query: gql`
          query GetAllImportantAlerts {
            allImportantAlerts {
              id
              title
              message
              targetAudience
              isActive
              createdAt
              createdByUserId
            }
          }
        `,
      fetchPolicy: 'network-only',
    })
      .pipe(
        map((result) => result.data?.allImportantAlerts ?? []),
        catchError((error) => {
          console.error('Error fetching all alerts:', error);
          return throwError(() => error);
        })
      );
  }

  getActiveAlerts(): Observable<ImportantAlert[]> {
    return this.apollo
      .query<{ activeImportantAlerts: ImportantAlert[] }>({
      query: gql`
          query GetActiveImportantAlerts {
            activeImportantAlerts {
              id
              title
              message
              targetAudience
              isActive
              createdAt
              createdByUserId
            }
          }
        `,
      fetchPolicy: 'network-only',
    })
      .pipe(
        map((result) => result.data?.activeImportantAlerts ?? []),
        catchError((error) => {
          console.error('Error fetching active alerts:', error);
          return throwError(() => error);
        })
      );
  }

  getActiveAlertsForUser(): Observable<ImportantAlert[]> {
    return this.apollo
      .query<{ activeImportantAlertsForUser: ImportantAlert[] }>({
      query: gql`
          query GetActiveImportantAlertsForUser {
            activeImportantAlertsForUser {
              id
              title
              message
              targetAudience
              isActive
              createdAt
              createdByUserId
            }
          }
        `,
      fetchPolicy: 'network-only',
    })
      .pipe(
        map((result) => result.data?.activeImportantAlertsForUser ?? []),
        catchError((error) => {
          console.error('Error fetching active alerts for user:', error);
          return throwError(() => error);
        })
      );
  }
}
